import { PROJECT_PIPELINE_STAGES, LEGACY_INSTALLATION_COMPLETED } from './projectConstants';

const LEAD_WORKFLOW_KEYS = new Set([
  'new', 'assigned', 'appointment_scheduled', 'site_visit', 'initial_quotation',
  'customer_acceptance', 'cust_acceptance', 'design', 'design_approval',
  'final_quotation', 'quotation_approval', 'project_started', 'project_start', 'drop',
]);

/**
 * Map old status so the stepper still shows a sensible position (post-installation).
 */
export function resolveStatusForProjectStepper(status) {
  if (!status) return 'Not started';
  const s = String(status).trim().toLowerCase();
  if (LEAD_WORKFLOW_KEYS.has(s)) return 'Not started';
  if (s === LEGACY_INSTALLATION_COMPLETED.toLowerCase()) return 'Documents';
  if (s === 'balance update') return 'Documents';
  if (s === 'documents approved') return 'Bill Approval';
  return status;
}

function stageIsDone(stageIndex, statusIdx, m, extras) {
  const name = PROJECT_PIPELINE_STAGES[stageIndex]?.toLowerCase() || '';
  const documentsStageComplete = !!extras.documentsStageComplete;

  if (name === 'not started') {
    return extras.pmAssigned || extras.teamAssigned || statusIdx > stageIndex;
  }
  if (name === 'team assignment') {
    return extras.teamAssigned || statusIdx > stageIndex;
  }
  if (name === 'carpenter complete') {
    return !!(m.carpenter || (m.carpenterProgress ?? 0) >= 100) || statusIdx > stageIndex;
  }
  if (name === 'electrician complete') {
    return !!(m.electrician || (m.electricianProgress ?? 0) >= 100) || statusIdx > stageIndex;
  }
  if (name === 'painter complete') {
    return !!(m.painter || (m.painterProgress ?? 0) >= 100) || statusIdx > stageIndex;
  }
  if (name === 'plumber complete') {
    return !!(m.plumber || (m.plumberProgress ?? 0) >= 100) || statusIdx > stageIndex;
  }
  if (name === 'documents') {
    return documentsStageComplete || statusIdx > stageIndex;
  }
  if (name === 'bill approval') {
    return statusIdx > stageIndex;
  }
  if (name === 'completed') {
    return statusIdx >= PROJECT_PIPELINE_STAGES.length - 1;
  }
  if (name === 'project started') {
    return !!(m.started) || statusIdx > stageIndex;
  }
  return statusIdx > stageIndex;
}

export function isPipelineStageCompleted(stageIndex, currentStatus, milestones = {}, extras = {}) {
  const resolved = resolveStatusForProjectStepper(currentStatus);
  const m = milestones || {};
  let statusIdx = PROJECT_PIPELINE_STAGES.findIndex(
    (s) => s.toLowerCase() === String(resolved).trim().toLowerCase()
  );
  if (statusIdx === -1) statusIdx = 0;

  return stageIsDone(stageIndex, statusIdx, m, extras);
}

export function getActivePipelineIndex(currentStatus, milestones = {}, extras = {}) {
  const resolved = resolveStatusForProjectStepper(currentStatus);
  const m = milestones || {};
  let statusIdx = PROJECT_PIPELINE_STAGES.findIndex(
    (s) => s.toLowerCase() === String(resolved).trim().toLowerCase()
  );
  if (statusIdx === -1) statusIdx = 0;

  for (let i = 0; i < PROJECT_PIPELINE_STAGES.length; i++) {
    if (!stageIsDone(i, statusIdx, m, extras)) {
      return i;
    }
    if (i === PROJECT_PIPELINE_STAGES.length - 1) return i;
  }
  return 0;
}

/** Human-readable pipeline label for list/detail views. */
export function getProjectPipelineDisplayStatus(project) {
  if (!project) return 'Not started';

  const pmAssigned = !!(project.projectManager && (
    Array.isArray(project.projectManager)
      ? project.projectManager.length > 0
      : project.projectManager._id || project.projectManager
  ));
  const teamAssigned = (project.fieldOfficers?.length || 0) > 0;
  const stored = project.projectStatus || project.status;
  const activeIdx = getActivePipelineIndex(stored, project.installationMilestones || {}, {
    documentsStageComplete: !!project.documentsStageComplete,
    teamAssigned,
    pmAssigned,
  });
  return PROJECT_PIPELINE_STAGES[activeIdx] || 'Not started';
}
